import axios from 'axios';
import { store } from '../reduxToolkit/store';
// ...existing code...
// Axios response interceptor for token refresh
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // Prevent retry for login and refresh endpoints
    const url = originalRequest.url || '';
    const isAuthRequest = url.includes('/auth/login') || url.includes('/auth/refresh');
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry &&
      !isAuthRequest
    ) {
      originalRequest._retry = true;
      try {
        const tokens = await refreshTokens();
        if (tokens && tokens.token) {
          // Update localStorage
          const persisted = localStorage.getItem('persist:eta-admin');
          if (persisted) {
            const parsed = JSON.parse(persisted);
            const auth = JSON.parse(parsed.auth);
            auth.token = tokens.token;
            auth.refreshToken = tokens.refreshToken;
            parsed.auth = JSON.stringify(auth);
            localStorage.setItem('persist:eta-admin', JSON.stringify(parsed));
          }
          // Update Redux state
          store.dispatch({ type: 'auth/setToken', payload: tokens });
          // Retry original request with new token
          originalRequest.headers['Authorization'] = `Bearer ${tokens.token}`;
          return axios(originalRequest);
        }
      } catch (refreshError) {
        // Optionally log error
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  },
);

export const getToken = () => {
  const persisted = localStorage.getItem('persist:eta-admin');
  const token = persisted && JSON.parse(JSON.parse(persisted).auth)?.token;
  return token;
};

export const refreshTokens = async () => {
  try {
    // Get refresh token from persisted state
    const persisted = localStorage.getItem('persist:eta-admin');
    const refreshToken = persisted && JSON.parse(JSON.parse(persisted).auth)?.refreshToken;
    const res = await axios.get(`${process.env.REACT_APP_BACKEND_API}/auth/refresh`, {
      withCredentials: true,
      headers: refreshToken ? { Authorization: `Bearer ${refreshToken}` } : {},
    });
    return res.data;
  } catch (error) {
    console.error('Error refreshing tokens on analytics:', error);
    throw error;
  }
};

export async function loginUser(data) {
  try {
    const res = await axios.post(`${process.env.REACT_APP_BACKEND_API}/auth/login`, data, {
      withCredentials: true,
    });
    return res.data;
  } catch (error) {
    if (error.response && error.response.data) {
      return error.response.data;
    }
    throw error;
  }
}

export async function logoutUser(data) {
  try {
    const res = await axios.post(`${process.env.REACT_APP_BACKEND_API}/auth/logout`, data, {
      withCredentials: true,
    });
    return res.data;
  } catch (error) {
    if (error.response && error.response.data) {
      return error.response.data;
    }
    throw error;
  }
}

export async function getAllVideos() {
  try {
    const token = getToken();
    const res = await axios.get(`${process.env.REACT_APP_BACKEND_API}/video`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      withCredentials: false, // set to true only if backend uses cookies
    });

    return res.data;
  } catch (error) {
    throw new Error('Failed to fetch videos');
  }
}

export const getStudentCourses = async (studentId) => {
  try {
    const token = getToken();
    const res = await axios.get(`${process.env.REACT_APP_BACKEND_API}/study/courses/${studentId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });

    return res.data;
  } catch (error) {
    console.error('Error fetching student courses:', error);
  }
};

export const getStudentAssignedCourses = async (studentId) => {
  try {
    const token = getToken();
    const res = await axios.get(
      `${process.env.REACT_APP_BACKEND_API}/study/assigned-courses/${studentId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );

    return res.data;
  } catch (error) {
    console.error('Error fetching student assigned courses:', error);
  }
};

export const getCourseLessons = async (courseId) => {
  try {
    const token = getToken();
    const res = await axios.get(`${process.env.REACT_APP_BACKEND_API}/courses/all-lessons`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      params: { courseId: courseId },
      withCredentials: false,
    });
    return res.data;
  } catch (error) {
    console.error('Error fetching student courses:', error);
  }
};

export const getLessonRelations = async (lessonId) => {
  try {
    const token = getToken();
    const res = await axios.get(
      `${process.env.REACT_APP_BACKEND_API}/courses/lesson/relations/${lessonId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );
    return res.data;
  } catch (error) {
    console.error('Error fetching lesson relations:', error);
  }
};

export const getAvailableCourses = async () => {
  try {
    const token = getToken();
    const res = await axios.get(`${process.env.REACT_APP_BACKEND_API}/courses/all`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });
    return res.data;
  } catch (error) {
    console.error('Error fetching available courses:', error);
  }
};

export const getStudentCourseLessonExams = async (body) => {
  try {
    const token = getToken();
    const res = await axios.get(
      `${process.env.REACT_APP_BACKEND_API}/study/exam/by-course-lesson/${body.studentId}/${body.courseId}/${body.lessonId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );

    return res.data;
  } catch (error) {
    console.log('getStudentCourseLessonExams error', error);
  }
};
export async function getChatHistory(threadId, after, before, currentVideoId) {
  try {
    const token = getToken();
    const res = await axios.get(`${process.env.REACT_APP_BACKEND_API}/openai/history`, {
      params: {
        threadId,
        after,
        before,
        videoFileId: currentVideoId,
      },
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });
    return res.data;
  } catch (error) {
    console.error('getChatHistory error', error);
    throw error;
  }
}

export async function getPrompts() {
  try {
    const token = getToken();
    const res = await axios.get(`${process.env.REACT_APP_BACKEND_API}/prompt`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });

    return res.data;
  } catch (error) {
    console.log('getPrompts error', error);
  }
}

export async function updatePrompts(name, value) {
  try {
    const token = getToken();
    const res = await axios.patch(
      `${process.env.REACT_APP_BACKEND_API}/prompt`,
      { name, value },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      },
    );
    return res.data;
  } catch (error) {
    console.log('updatePrompts error', error);
  }
}

export const bookStudentExam = async (data) => {
  try {
    const token = getToken();
    const res = await axios.post(`${process.env.REACT_APP_BACKEND_API}/study/exam`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });

    return res.data;
  } catch (error) {
    console.log('bookStudentExam error', error);
    throw error;
  }
};
export const getEditCourseData = async (courseId) => {
  try {
    const token = getToken();
    const res = await axios.get(`${process.env.REACT_APP_BACKEND_API}/courses/${courseId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });
    return res.data;
  } catch (error) {
    console.log('bookStudentExam error', error);
  }
};

export const getEditLessonData = async (lessonId) => {
  try {
    const token = getToken();
    const res = await axios.get(`${process.env.REACT_APP_BACKEND_API}/courses/lesson/${lessonId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });
    return res.data;
  } catch (error) {
    console.log('bookStudentExam error', error);
  }
};

export const createBot = async (data) => {
  try {
    const token = getToken();
    const res = await axios.post(
      `${process.env.REACT_APP_BACKEND_API}/assistant/bot/create`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );
    return res.data;
  } catch (error) {
    console.log('createBot error', error);
  }
};

export const leaveMeetingBot = async (id) => {
  try {
    const token = getToken();
    const res = await axios.post(
      `${process.env.REACT_APP_BACKEND_API}/assistant/bot/leave`,
      {
        botId: id,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );
    return res.data;
  } catch (error) {
    console.log('error', error);
  }
};
export const getBotTranscriptionFiles = async () => {
  try {
    const token = getToken();
    const res = await axios.get(`${process.env.REACT_APP_BACKEND_API}/assistant/bot/files`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });
    return res.data;
  } catch (error) {
    console.log('error', error);
  }
};

export const getBotTranscriptionFilesByUser = async (id) => {
  try {
    const token = getToken();
    const res = await axios.get(
      `${process.env.REACT_APP_BACKEND_API}/assistant/bot/userfiles/${id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );
    return res.data;
  } catch (error) {
    console.log('error', error);
  }
};

export const getBotTranscriptionSingeFile = async (id) => {
  try {
    const token = getToken();
    const res = await axios.get(`${process.env.REACT_APP_BACKEND_API}/assistant/bot/files/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });
    return res.data;
  } catch (error) {
    console.log('error', error);
  }
};

export const updateTranscriptionText = async (id, update) => {
  try {
    const token = getToken();
    const res = await axios.put(
      `${process.env.REACT_APP_BACKEND_API}/assistant/bot/files/${id}`,
      update,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );
    return res.data;
  } catch (error) {
    console.log('error', error);
  }
};

export const recoverTranscriptionFile = async (id) => {
  try {
    const token = getToken();
    const res = await axios.get(
      `${process.env.REACT_APP_BACKEND_API}/assistant/bot/recover/${id}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );
    return res.data.data;
  } catch (error) {
    console.log('recoverTranscriptionFile error', error);
    throw error;
  }
};

export const deleteTranscriptionItem = async (transcriptionId, deleteItemId) => {
  try {
    const token = getToken();
    const res = await axios.delete(
      `${process.env.REACT_APP_BACKEND_API}/assistant/bot/files/${transcriptionId}`,
      {
        data: { deleteItemId: deleteItemId },
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );
    return res.data;
  } catch (error) {
    console.log('error', error);
  }
};
export const getUsers = async () => {
  try {
    const token = getToken();
    const res = await axios.get(`${process.env.REACT_APP_BACKEND_API}/users`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });
    return res.data;
  } catch (error) {
    console.log('error', error);
  }
};

export const getSingleUserWithStats = async (id, from, to) => {
  try {
    const token = getToken();
    const res = await axios.get(`${process.env.REACT_APP_BACKEND_API}/dashboard/user-stat/${id}`, {
      params: { from: from, to: to },
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });
    return res.data;
  } catch (error) {
    console.log('getSingleUserWithStats error', error);
  }
};

export const getUsersForMeeting = async (googleMeetLink) => {
  try {
    const token = getToken();
    const res = await axios.get(`${process.env.REACT_APP_BACKEND_API}/users`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });
    return res.data;
  } catch (error) {
    console.log('error', error);
  }
};

export const getExamDetails = async (id) => {
  try {
    const token = getToken();
    const res = await axios.get(
      `${process.env.REACT_APP_BACKEND_API}/assistant/exam/details/${id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );
    return res.data;
  } catch (error) {
    console.log('error', error);
  }
};

export const sendFeedback = async (data) => {
  try {
    const token = getToken();
    const res = await axios.post(`${process.env.REACT_APP_BACKEND_API}/feedback/`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });
    return res.data;
  } catch (error) {
    console.log('error', error);
  }
};

export const getAllFeedBacks = async () => {
  try {
    const token = getToken();
    const res = await axios.get(`${process.env.REACT_APP_BACKEND_API}/feedback/all`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });
    return res.data;
  } catch (error) {
    console.log('error', error);
  }
};

export const importUsers = async (formData) => {
  try {
    const token = getToken();
    const res = await axios.post(`${process.env.REACT_APP_BACKEND_API}/users/import`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      withCredentials: false,
    });
    return res.data;
  } catch (error) {
    console.log('error', error);
  }
};

export const getDashboardOverviewData = async (from, to) => {
  try {
    const token = getToken();
    const res = await axios.get(`${process.env.REACT_APP_BACKEND_API}/dashboard/overview`, {
      params: {
        from,
        to,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
      withCredentials: false,
    });
    return res.data;
  } catch (error) {
    console.log('getDashboardOverviewData error', error);
  }
};
export const getUserDayActivity = async (userId, date) => {
  try {
    const token = getToken();
    const res = await axios.get(
      `${process.env.REACT_APP_BACKEND_API}/dashboard/user-stat/${userId}/day`,
      {
        params: { date },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return res.data;
  } catch (error) {
    console.log('getUserDayActivity error', error);
  }
};

export const getCalendarStopPeriods = async () => {
  try {
    const token = getToken();
    const res = await axios.get(`${process.env.REACT_APP_BACKEND_API}/calendar/stop-periods`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error) {
    console.log('getCalendarStopPeriods error', error);
  }
};

export const addCalendarStopPeriods = async (period) => {
  try {
    const token = getToken();
    const res = await axios.post(
      `${process.env.REACT_APP_BACKEND_API}/calendar/stop-periods`,
      period,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return res.data;
  } catch (error) {
    console.log('addCalendarStopPeriods error', error);
  }
};
export const deleteCalendarStopPeriods = async (id, date, time) => {
  try {
    const token = getToken();
    const body = {
      date,
      time,
    };
    const res = await axios.patch(
      `${process.env.REACT_APP_BACKEND_API}/calendar/stop-periods/delete/${id}`,
      body,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return res.data;
  } catch (error) {
    console.log('deleteCalendarStopPeriods error', error);
  }
};

export const initiateScoring = async (data) => {
  try {
    const token = getToken();
    const res = await axios.post(`${process.env.REACT_APP_BACKEND_API}/score/new`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error) {
    console.log('initiateScoring error', error);
  }
};

export const updateScoring = async (data) => {
  try {
    const token = getToken();
    const res = await axios.post(`${process.env.REACT_APP_BACKEND_API}/score/edit`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error) {
    console.log('updateScoring error', error);
  }
};
export const importLeads = async (formData) => {
  try {
    const token = getToken();
    const res = await axios.post(`${process.env.REACT_APP_BACKEND_API}/leads/import`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      withCredentials: false,
    });
    return res.data;
  } catch (error) {
    console.log('importLeads error', error);
  }
};

export const getLeadsCounts = async () => {
  try {
    const token = getToken();
    const res = await axios.get(`${process.env.REACT_APP_BACKEND_API}/leads/counts`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      withCredentials: false,
    });
    return res.data;
  } catch (error) {
    console.log('getLeadsCounts error', error);
  }
};

export const sendTestQueueJob = async (body) => {
  try {
    const token = getToken();
    const res = await axios.post(
      `${process.env.REACT_APP_BACKEND_API}/assistant/make-test-score`,
      body,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return res.data;
  } catch (error) {
    console.log('sendTestQueueJob error', error);
  }
};

export const getBotIdFromDB = async (assistantExamId) => {
  try {
    const token = getToken();
    const res = await axios.get(`${process.env.REACT_APP_BACKEND_API}/assistant/bot/bot-id`, {
      params: { assistantExamId },
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });
    return res.data;
  } catch (error) {
    console.log('getBotIdFromDB error', error);
  }
};
export const getCrmChartsData = async (from, to, timezone) => {
  try {
    const token = getToken();
    const res = await axios.get(`${process.env.REACT_APP_BACKEND_API}/crm-control/crm-charts`, {
      params: {
        from,
        to,
        timezone,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
      withCredentials: false,
    });
    return res.data;
  } catch (error) {
    console.log('getCrmChartsData error', error);
  }
};

export const getCrmLeadsChartsData = async (from, to, timezone) => {
  try {
    const token = getToken();
    const res = await axios.get(`${process.env.REACT_APP_BACKEND_API}/crm-control/leads-charts`, {
      params: {
        from,
        to,
        timezone,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
      withCredentials: false,
    });
    return res.data;
  } catch (error) {
    console.log('getCrmLeadsChartsData error', error);
  }
};

// Cached fetch for cancel lead reasons — rarely changes so we keep a short TTL
const _cancelLeadReasonsCache = {
  data: null,
  expiresAt: 0,
  promise: null,
};

export const getCancelLeadReasons = async () => {
  try {
    const token = getToken();
    const res = await axios.get(`${process.env.REACT_APP_BACKEND_API}/leads/cancel-reasons`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      withCredentials: false,
    });
    return res.data;
  } catch (error) {
    console.log('getCancelLeadReasons error', error);
  }
};

export const getCancelEngagementReasons = async () => {
  try {
    const token = getToken();
    const res = await axios.get(`${process.env.REACT_APP_BACKEND_API}/engagements/cancel-reasons`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      withCredentials: false,
    });
    return res.data;
  } catch (error) {
    console.log('getCancelEngagementReasons error', error);
  }
};

export const getClosedLeadEngagements = async ({ from, to, timezone }) => {
  try {
    const token = getToken();
    const res = await axios.get(
      `${process.env.REACT_APP_BACKEND_API}/crm-control/leads-engagements-closed-charts`,
      {
        params: {
          from,
          to,
          timezone,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: false,
      },
    );
    return res.data;
  } catch (error) {
    console.log('getClosedLeadEngagements error', error);
  }
};
